/**
 * Voucher Templates Service
 * CRUD operations for voucher templates
 */

import { supabase } from "./supabase";
import type {
  VoucherTemplate,
  CreateVoucherTemplateDTO,
  UpdateVoucherTemplateDTO,
} from "../types/voucherTemplate.types";

/**
 * Get all voucher templates
 */
export async function getVoucherTemplates(): Promise<VoucherTemplate[]> {
  const { data, error } = await supabase
    .from("voucher_templates")
    .select("*")
    .is("deleted_at", null)
    .order("created_at", { ascending: false });

  if (error) {
    console.error("Error fetching voucher templates:", error);
    throw new Error("Failed to fetch voucher templates");
  }

  return data || [];
}

/**
 * Get a single voucher template by ID
 */
export async function getVoucherTemplate(
  id: string,
): Promise<VoucherTemplate | null> {
  const { data, error } = await supabase
    .from("voucher_templates")
    .select("*")
    .eq("id", id)
    .is("deleted_at", null)
    .single();

  if (error) {
    console.error("Error fetching voucher template:", error);
    throw new Error("Failed to fetch voucher template");
  }

  return data;
}

/**
 * Create a new voucher template
 */
export async function createVoucherTemplate(
  template: CreateVoucherTemplateDTO,
): Promise<VoucherTemplate> {
  const { data, error } = await supabase
    .from("voucher_templates")
    .insert([template])
    .select()
    .single();

  if (error) {
    console.error("Error creating voucher template:", error);
    throw new Error("Failed to create voucher template");
  }

  return data;
}

/**
 * Update a voucher template
 */
export async function updateVoucherTemplate(
  id: string,
  updates: UpdateVoucherTemplateDTO,
): Promise<VoucherTemplate> {
  const { data, error } = await supabase
    .from("voucher_templates")
    .update(updates)
    .eq("id", id)
    .is("deleted_at", null)
    .select()
    .single();

  if (error) {
    console.error("Error updating voucher template:", error);
    throw new Error("Failed to update voucher template");
  }

  return data;
}

/**
 * Soft delete a voucher template
 */
export async function deleteVoucherTemplate(id: string): Promise<void> {
  const { error } = await supabase
    .from("voucher_templates")
    .update({ deleted_at: new Date().toISOString() })
    .eq("id", id);

  if (error) {
    console.error("Error deleting voucher template:", error);
    throw new Error("Failed to delete voucher template");
  }
}

/**
 * Search voucher templates by name or promo code
 */
export async function searchVoucherTemplates(
  query: string,
): Promise<VoucherTemplate[]> {
  const { data, error } = await supabase
    .from("voucher_templates")
    .select("*")
    .is("deleted_at", null)
    .or(`voucher_name.ilike.*${query}*,voucher_promo_code.ilike.*${query}*`)
    .order("created_at", { ascending: false })
    .limit(20);

  if (error) {
    console.error("Error searching voucher templates:", error);
    throw new Error("Failed to search voucher templates");
  }

  return data || [];
}

/**
 * Get voucher templates by type
 */
export async function getVoucherTemplatesByType(
  type: string,
): Promise<VoucherTemplate[]> {
  const { data, error } = await supabase
    .from("voucher_templates")
    .select("*")
    .eq("voucher_type", type)
    .is("deleted_at", null)
    .order("created_at", { ascending: false });

  if (error) {
    console.error("Error fetching voucher templates by type:", error);
    throw new Error("Failed to fetch voucher templates by type");
  }

  return data || [];
}
